import apiClient from '../api/apiClient';
import { API_BASE_URL } from '../utils/constants';

class AdiParseService {
  constructor() {
    this.apiBaseUrl = API_BASE_URL;
    this.network = 'mainnet';
    this.cache = new Map();
    this.pendingRequests = new Map(); // Track ongoing requests to prevent duplicates
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout
  }

  setApiBaseUrl(url) {
    this.apiBaseUrl = url;
  }

  setNetwork(network) {
    this.network = network;
  }

  clearCache(identity = null) {
    if (identity) {
      this.cache.delete(identity);
      this.pendingRequests.delete(identity);
    } else {
      this.cache.clear();
      this.pendingRequests.clear();
    }
  }

  // Check if cache entry is still valid
  isCacheValid(cacheEntry) {
    return cacheEntry && (Date.now() - cacheEntry.timestamp) < this.cacheTimeout;
  }

  // Extract network name from identity (remove everything after .acme)
  extractNetworkName(identity) {
    if (!identity) return '';
    // Remove protocol if present
    const clean = identity.replace('acc://', '');
    // Get everything before .acme
    const parts = clean.split('.acme');
    return parts[0];
  }

  // Generate BankOnLedger URL
  getBankOnLedgerUrl(identity) {
    const networkName = this.extractNetworkName(identity);
    return `https://${networkName}.BankOnLedger.com/?current-network=${this.network}`;
  }

  // Generate Qoboto URL
  getQobotoUrl(identity) {
    const networkName = this.extractNetworkName(identity);
    return `https://${networkName}.Qoboto.com/?current-network=${this.network}`;
  }

  // Get data from Qoboto API with optimizations
  async getQobotoData(identity, abortController = null) {
    const cacheKey = `${identity}_${this.network}`;

    // Check cache first with timestamp validation
    if (this.cache.has(cacheKey)) {
      const cacheEntry = this.cache.get(cacheKey);
      if (this.isCacheValid(cacheEntry)) {
        console.log(`Cache hit for ${identity}`);
        return cacheEntry.data;
      } else {
        // Remove expired cache entry
        this.cache.delete(cacheKey);
      }
    }

    // For simplicity, remove request deduplication for now to avoid hanging
    // Each request will be independent
    console.log(`Making fresh API request for ${identity}`);

    // Strip everything after .acme for the API call
    const cleanIdentity = identity.includes('.acme')
      ? identity.split('.acme')[0] + '.acme'
      : identity;

    try {
      const mainSection = await this.makeApiRequest(cleanIdentity, abortController);

      // Cache with timestamp only if request completed successfully
      if (mainSection) {
        this.cache.set(cacheKey, {
          data: mainSection,
          timestamp: Date.now()
        });
      }

      console.log(`API call completed for ${identity}`);
      return mainSection;
    } catch (error) {
      // Check if error is due to request cancellation
      if (error.name === 'CanceledError' || error.message === 'canceled' || error.code === 'ERR_CANCELED') {
        console.log(`Request cancelled for ${identity}`);
        // Don't throw for cancelled requests - this is expected behavior
        return null;
      }
      console.error('Error fetching Qoboto data:', error);
      throw new Error(`Failed to fetch data for ${identity}: ${error.message}`);
    }
  }

  // Separate method for making the actual API request
  async makeApiRequest(cleanIdentity, abortController) {
    const config = {};
    if (abortController) {
      config.signal = abortController.signal;
    }

    const response = await apiClient.get(
      `/api/v1/Qoboto/GetDataValue/All?DataAccountUrl=${cleanIdentity}`,
      config
    );

    if (response.data && response.data) {
      // Find the "main" section
      const mainSection = response.data.find(section =>
        section.name && section.name.toLowerCase() === 'main'
      );
      return mainSection;
    }

    return null;
  }

  // Get logo URL from API
  async getLogoUrl(identity) {
    try {
      const mainData = await this.getQobotoData(identity);
      return mainData?.logoUrl || null;
    } catch (error) {
      console.error('Error getting logo URL:', error);
      return null;
    }
  }

  // Get description from main section
  async getSectionMain1Description(identity) {
    try {
      const mainData = await this.getQobotoData(identity);
      return mainData?.sectionMain1Description || null;
    } catch (error) {
      console.error('Error getting description:', error);
      return null;
    }
  }

  // Get background image URL
  async getSectionMain1Background2ImageUrl(identity) {
    try {
      const mainData = await this.getQobotoData(identity);
      return mainData?.sectionMain1Background2ImageUrl || null;
    } catch (error) {
      console.error('Error getting background image URL:', error);
      return null;
    }
  }

  // Get all main data in one call (efficient)
  async getAllMainData(identity, abortController = null) {
    try {
      const mainData = await this.getQobotoData(identity, abortController);

      // Even if mainData is null/undefined, we still return a valid object
      // This way the component knows the request completed but had no data
      return {
        logoUrl: mainData?.logoUrl || null,
        sectionMain1Description: mainData?.sectionMain1Description || null,
        sectionMain1Background2ImageUrl: mainData?.sectionMain1Background2ImageUrl || null,
        bankOnLedgerUrl: this.getBankOnLedgerUrl(identity),
        qobotoUrl: this.getQobotoUrl(identity),
        networkName: this.extractNetworkName(identity),
        hasData: !!mainData // Add flag to indicate if we actually got data
      };
    } catch (error) {
      // Check if error is due to request cancellation
      if (error.name === 'CanceledError' || error.message === 'canceled' || error.message?.includes('canceled')) {
        console.log(`getAllMainData cancelled for ${identity}`);
        return null; // Only return null for actual cancellations
      }
      console.error('Error getting all main data:', error);
      throw error;
    }
  }
}

// Create singleton instance
const adiParseService = new AdiParseService();
export default adiParseService;